/**
 * "Record repair for <tech>" — the office's way to unstick a repair job.
 *
 * Job #092678199 (2026-10-01): the repair was done on site, but the tech had
 * no photos, so the repair screen would not save, and a repair job cannot
 * complete without the repair row. The office's only way out was posting the
 * repair from its own login, which credited the office user with the repair
 * and its points.
 *
 * This records the repair FOR the tech: performed_by is the tech picked here
 * (defaulting to the job's tech), the office user is recorded as the person who
 * entered it, the no-photos reason is filled in automatically, and the repair
 * point goes to the tech. It never overwrites a repair the tech saved.
 * Mockup: mockups/repair-without-photos.html (approved 2026-10-01).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Wrench } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { apiErrorInfo, techLabel, type JobAssetRow, type TechOption } from "./types";

const MCKINNEY_ACTIONS = ["Cleaned", "Replaced"] as const;

export function RecordRepairDialog({
    open,
    onClose,
    jobId,
    asset,
    techs,
    defaultTechId,
    isMckinneySite,
}: {
    open: boolean;
    onClose: () => void;
    jobId: string;
    asset: JobAssetRow | null;
    techs: TechOption[];
    /** The job's assigned tech — who did the repair in nearly every case. */
    defaultTechId?: string | null;
    isMckinneySite: boolean;
}) {
    const qc = useQueryClient();
    const [techId, setTechId] = useState("");
    const [notes, setNotes] = useState("");
    const [mckinneyAction, setMckinneyAction] = useState<string>("");

    const performedBy = techId || defaultTechId || "";
    const tech = techs.find((t) => t.id === performedBy);
    const techName = tech ? techLabel(tech) : null;

    const record = useMutation({
        mutationFn: () =>
            api.post<{ points?: { recorded: boolean; message: string | null }; performedByName?: string | null }>(
                `/api/backflow/job/${jobId}/devices/${asset?.asset_id}/office-repair`,
                {
                    performedByUserId: performedBy,
                    repairNotes: notes.trim(),
                    mckinneyRepairAction: isMckinneySite ? mckinneyAction || null : null,
                },
            ),
        onSuccess: (res) => {
            const who = res?.performedByName ?? techName ?? "the tech";
            toast.success(`Repair recorded for ${who}.`);
            if (res?.points && !res.points.recorded && res.points.message) {
                toast.warning(res.points.message, { duration: 12_000, closeButton: true });
            }
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["backflow", "job", jobId, "repairs"] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            setNotes("");
            setMckinneyAction("");
            setTechId("");
            onClose();
        },
        onError: (e: unknown) => {
            const { message, error } = apiErrorInfo(e);
            toast.error(message ?? `Couldn't record the repair: ${error ?? "unknown error"}`);
        },
    });

    const canSubmit =
        !!asset &&
        !!performedBy &&
        notes.trim().length > 0 &&
        (!isMckinneySite || !!mckinneyAction) &&
        !record.isPending;

    return (
        <Dialog open={open} onClose={onClose} title={`Record repair — ${asset?.label ?? "device"}`}>
            <div className="space-y-4 px-5 py-4">
                <p className="text-sm text-foreground/80">
                    For when the repair was done on site but never saved in the app.
                    {asset?.serial_number && <span className="font-mono"> S/N {asset.serial_number}.</span>}
                </p>

                <div className="space-y-1.5">
                    <Label htmlFor="office-repair-tech">Performed by *</Label>
                    <select
                        id="office-repair-tech"
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={performedBy}
                        onChange={(e) => setTechId(e.target.value)}
                    >
                        <option value="">— select the technician —</option>
                        {techs.map((t) => (
                            <option key={t.id} value={t.id}>
                                {techLabel(t)}
                            </option>
                        ))}
                    </select>
                    <p className="text-[11px] text-foreground/60">
                        Defaults to the job's tech. Pick whoever actually did the work.
                    </p>
                </div>

                {isMckinneySite && (
                    <div className="space-y-1.5">
                        <Label htmlFor="office-repair-mckinney">Cleaned or replaced? *</Label>
                        <div className="flex gap-2" id="office-repair-mckinney">
                            {MCKINNEY_ACTIONS.map((a) => (
                                <Button
                                    key={a}
                                    type="button"
                                    size="sm"
                                    variant={mckinneyAction === a ? "default" : "outline"}
                                    onClick={() => setMckinneyAction(a)}
                                >
                                    {a}
                                </Button>
                            ))}
                        </div>
                        <p className="text-[11px] text-foreground/60">
                            McKinney site — fills the CLEANED/REPLACED boxes on the report.
                        </p>
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label htmlFor="office-repair-notes">Repair notes *</Label>
                    <Textarea
                        id="office-repair-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="What was repaired, as the tech described it"
                        rows={3}
                    />
                </div>

                <p className="rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Saved with no photos, marked “Recorded from the office”.
                </p>

                {techName && (
                    <p className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            The repair and its points are credited to <span className="font-semibold">{techName}</span>.
                            You're recorded as the person who entered it.
                        </span>
                    </p>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={record.isPending}>
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} onClick={() => record.mutate()}>
                        {record.isPending ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                            <Wrench className="mr-1 h-4 w-4" />
                        )}
                        Record repair
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
